/**
 * Social Security Taxation tile (IRC §86, IRS Pub. 915). "How much of my Social
 * Security is taxable?" Answers the question most retirees get wrong — neither 0%
 * nor 100% but a sliding 0/50/85% set by *provisional income* against two
 * statutory (never-indexed) base amounts. Gates on the social-security-taxation
 * shard; every base amount carries the §86 citation. Companion to the Social
 * Security Claiming Age tile in the same hub.
 */
import { Money } from "../engine/money";
import { socialSecurityBenefitTaxation } from "../engine/socialSecurityTax";
import type { FilingStatus } from "../data/schemas";
import { el, option } from "../ui/dom";
import { field, parseNonNegative, pct, tryExampleButton } from "../ui/form";
import { resultCard, type BreakdownLine } from "../ui/resultCard";
import type { SituationStore } from "../profile/situation";
import type { TileContext, TileDefinition } from "./types";

// Single / head of household / qualifying surviving spouse share the $25k/$34k
// bases; married jointly uses $32k/$44k.
//
// The fourth option is the one that used to be missing. §86(c)(1)(C)(ii) gives a
// married individual filing separately a base amount of ZERO unless they lived
// apart from their spouse for the whole year — up to 85% of the benefit is
// taxable from the first dollar, with no exempt tier at all. It was documented
// here as "left out — see the shard note", which is a scope decision that reads
// fine in a comment and does something else on screen: a reader in that case
// found no option describing them, picked Single, and was told nothing was
// taxable below $25,000 of provisional income. Silently dropping an audience
// costs more than naming them, and the amounts are two zeroes.
//
// A separate filer who DID live apart all year uses the single amounts under
// §86(c)(1)(C)(i), which is what the note under the select says.
const STATUSES: {
  value: "single" | "married_jointly" | "head_of_household" | "married_separately";
  label: string;
}[] = [
  { value: "single", label: "Single" },
  { value: "married_jointly", label: "Married filing jointly" },
  { value: "head_of_household", label: "Head of household" },
  { value: "married_separately", label: "Married filing separately, lived with spouse" },
];

interface Fields {
  fs: "single" | "married_jointly" | "head_of_household" | "married_separately";
  ss: number;
  other: number;
  exempt: number;
}

const EXAMPLE: Fields = { fs: "single", ss: 24000, other: 30000, exempt: 0 };

function isStatus(v: string): v is Fields["fs"] {
  return STATUSES.some((s) => s.value === v);
}

/**
 * The filing status this tile opens on: the link, then My Situation, then single.
 *
 * Every other calculator with a "Filing status" control pre-fills it from the
 * shared profile; this one defaulted to single and asked again. That is not a
 * missing convenience on a tile whose whole answer is a pair of base amounts:
 * a joint filer who had told the site so was measured against $25,000/$34,000
 * instead of $32,000/$44,000, so the tile's headline -- how much of the benefit
 * is taxable -- was wrong by default for the reader who had already answered.
 *
 * Qualifying surviving spouse is the fifth status and has no entry in the
 * four-value select: §86(c)(1)(A) puts it on the single base amounts, which is
 * what the comment above STATUSES says, so it seeds "Single" and computes the
 * right money. Nothing is written back -- this tile reads the shared field and
 * does not assert one, so a four-value control can never narrow a five-value
 * field the way Education Credits' two-value checkbox once did.
 */
function seedStatus(p: URLSearchParams, profile: SituationStore): Fields["fs"] {
  const fs = p.get("fs");
  if (fs && isStatus(fs)) return fs;
  const stored = profile.get("filingStatus");
  if (stored && isStatus(stored)) return stored;
  return "single";
}

function readFields(p: URLSearchParams, profile: SituationStore): Fields {
  return {
    fs: seedStatus(p, profile),
    ss: parseNonNegative(p.get("ss"), 0),
    other: parseNonNegative(p.get("oi"), 0),
    exempt: parseNonNegative(p.get("ti"), 0),
  };
}

function writeFields(f: Fields): URLSearchParams {
  const p = new URLSearchParams();
  p.set("fs", f.fs);
  p.set("ss", String(f.ss));
  if (f.other > 0) p.set("oi", String(f.other));
  if (f.exempt > 0) p.set("ti", String(f.exempt));
  return p;
}

export function mountSocialSecurityTax(ctx: TileContext): void {
  const { root, data } = ctx;
  root.replaceChildren();
  const params = data?.socialSecurityTaxation();
  if (!params) {
    root.append(
      el("div", {
        class: "verify-banner",
        attrs: { role: "alert" },
        text: "Social Security taxation data is unavailable, verify before relying on any figure.",
      }),
    );
    return;
  }
  const ssTax = params;
  let fields = readFields(ctx.params, ctx.profile);

  const fsSelect = el(
    "select",
    { name: "fs", attrs: { "aria-label": "Filing status" } },
    ...STATUSES.map((s) => option(s.value, s.label, s.value === fields.fs)),
  );
  // The element's value is authoritative for the intended status: `recompute()`
  // reads `fsSelect.value`, so leaving it to the options' `selected` attributes
  // lets a later read pick up a default the tile never intended.
  fsSelect.value = fields.fs;
  fsSelect.value = fields.fs;
  const mkNum = (name: string, label: string, value: number): HTMLInputElement =>
    el("input", {
      type: "number",
      name,
      min: 0,
      step: 1000,
      value,
      attrs: { "aria-label": label, inputmode: "decimal" },
    });
  const ssInput = mkNum("ss", "Annual Social Security benefits", fields.ss);
  const otherInput = mkNum("oi", "Other income (AGI excluding Social Security)", fields.other);
  const exemptInput = mkNum("ti", "Tax-exempt interest", fields.exempt);

  const resultContainer = el("div", { class: "tile-result", attrs: { "aria-live": "polite" } });

  function compute(): void {
    const status = fields.fs as FilingStatus;
    const base1 = ssTax.base1ByFilingStatus[status] ?? ssTax.base1ByFilingStatus.single ?? 0;
    const base2 = ssTax.base2ByFilingStatus[status] ?? ssTax.base2ByFilingStatus.single ?? 0;
    const r = socialSecurityBenefitTaxation(
      {
        socialSecurityBenefits: fields.ss,
        otherIncome: fields.other,
        taxExemptInterest: fields.exempt,
      },
      {
        base1,
        base2,
        tier1InclusionRate: ssTax.tier1InclusionRate,
        tier2InclusionRate: ssTax.tier2InclusionRate,
      },
    );
    const fmt = (m: Money): string => m.format(ctx.locale);
    const tierNote =
      r.tier === "none"
        ? `Your provisional income is at or below $${base1.toLocaleString()}, so none of your Social Security is taxable.`
        : r.tier === "up-to-50"
          ? `Your provisional income is between $${base1.toLocaleString()} and $${base2.toLocaleString()}, so up to half of your benefit is taxable.`
          : `Your provisional income is above $${base2.toLocaleString()}, so up to 85% of your benefit is taxable — Social Security is never more than 85% taxable.`;
    const lines: BreakdownLine[] = [
      {
        label: "Provisional income (other income + tax-exempt interest + ½ benefits)",
        value: fmt(r.provisionalIncome),
        citation: ssTax.citation,
      },
      { label: "Taxable portion of your benefit", value: fmt(r.taxableBenefits), emphasis: true },
      { label: "Tax-free portion of your benefit", value: fmt(r.nonTaxableBenefits) },
      {
        label: "Share of your benefit that's taxable",
        value: r.percentTaxable > 0 ? pct(r.percentTaxable, 1) : "0%",
      },
      { label: "How the threshold works", value: tierNote, citation: ssTax.citation },
      ...(fields.fs === "married_separately"
        ? [
            {
              label: "Why there is no exempt tier",
              value:
                "A separate return carries a base amount of zero unless you lived apart from " +
                "your spouse for the entire year, so the benefit is taxable from the first " +
                "dollar of provisional income. If you did live apart all year, use Single — the " +
                "$25,000 and $34,000 amounts are the ones that apply to you.",
              citation: ssTax.citation,
            },
          ]
        : []),
    ];

    resultContainer.replaceChildren(
      resultCard({
        label: "Taxable Social Security benefits",
        value: r.taxableBenefits,
        locale: ctx.locale,
        breakdown: lines,
        permalink: () => ctx.permalink(writeFields(fields)),
      }),
    );
  }

  function recompute(): void {
    fields = {
      fs: isStatus(fsSelect.value) ? fsSelect.value : "single",
      ss: parseNonNegative(ssInput.value, 0),
      other: parseNonNegative(otherInput.value, 0),
      exempt: parseNonNegative(exemptInput.value, 0),
    };
    ctx.setParams(writeFields(fields));
    compute();
  }

  fsSelect.addEventListener("change", recompute);
  for (const i of [ssInput, otherInput, exemptInput]) i.addEventListener("input", recompute);

  const tryExample = tryExampleButton(() => {
    fields = { ...EXAMPLE };
    fsSelect.value = fields.fs;
    ssInput.value = String(fields.ss);
    otherInput.value = String(fields.other);
    exemptInput.value = String(fields.exempt);
    recompute();
  });

  const form = el(
    "form",
    { class: "tile-form", on: { submit: (e) => e.preventDefault() } },
    field("Filing status", fsSelect),
    field("Annual Social Security benefits", ssInput),
    field("Other income (your AGI excluding Social Security)", otherInput),
    field("Tax-exempt interest (e.g. municipal bonds)", exemptInput),
    el("div", { class: "tile-form-actions" }, tryExample),
  );

  root.append(form, resultContainer);
  compute();
}

export const socialSecurityTaxTile: TileDefinition = {
  id: "social-security-tax",
  title: "Social Security Taxation",
  pillar: "retirement",
  description: "How much of your Social Security benefit is taxable (the IRC §86 0/50/85% rule).",
  keywords: [
    "social security tax",
    "social security taxable",
    "taxable benefits",
    "provisional income",
    "combined income",
    "85%",
    "irc 86",
    "section 86",
    "pub 915",
    "retirement income",
    "is my social security taxed",
  ],
  status: "ready",
  how: "Most people think Social Security is either fully taxed or not taxed at all. It's neither: a sliding 0%, up to 50%, or up to 85% of your benefit is taxable, depending on your 'provisional income' — your other income (your AGI without Social Security) plus any tax-exempt interest plus half of your benefits.\n\nThere are two base amounts (single $25,000 / $34,000; married jointly $32,000 / $44,000). Below the first, none of your benefit is taxable. Between the two, up to half is. Above the second, up to 85% is — and 85% is the most that's ever taxable, no matter how high your income. These thresholds are written into the law (IRC §86) and have never been adjusted for inflation, so the same numbers apply every year, which is why more retirees cross them over time.\n\nEnter your annual benefit, your other income, and any tax-exempt interest. We follow the IRS Publication 915 worksheet exactly to show how much of your benefit lands in taxable income — that taxable portion is then taxed at your ordinary rate, so pair it with the Take-Home or Federal Income Tax tools to see the dollars.",
  resources: [
    {
      label: "IRS Publication 915 (Social Security benefits)",
      url: "https://www.irs.gov/pub/irs-pdf/p915.pdf",
    },
    {
      label: "SSA, income taxes and your Social Security benefit",
      url: "https://www.ssa.gov/manage-benefits/request-withhold-taxes",
    },
    {
      label: "26 U.S. Code §86 (taxation of Social Security benefits)",
      url: "https://www.law.cornell.edu/uscode/text/26/86",
    },
  ],
  related: [
    {
      hubId: "retirement",
      tool: "social-security",
      label: "Social Security Claiming Age",
      note: "what your benefit is at 62, full retirement age, and 70",
    },
    {
      hubId: "paycheck-taxes",
      tool: "federal-income-tax",
      label: "Federal Income Tax",
      note: "the tax on the taxable portion at your ordinary rate",
    },
  ],
  mount: mountSocialSecurityTax,
};
